/**
 * SingleBioMarkerResult Component
 *
 * Description: This component renders a form to capture and display single biomarker results.
 * It includes fields for selecting dates, showing reference/optional values, and entering results.
 * The form is dynamically adjusted based on the biomarker type (e.g., DND, PN).
 *
 * File Name: SingleBioMarkerResult.js
 * Date: 2024-08-24
 */

import React, { FormEvent, useEffect, useState } from "react";
import { IAddSingleBio } from "src/types/GlobalInterfaces";
import PrimaryBtn from "../primaryBtn";
import moment from "moment";
import {
  ageCategory,
  validateNotFutureDate,
} from "src/helpers/helperFunctions";
import {
  descriptiveResult,
  twoBarDNDAsc,
  twoBarDNDDesc,
  twoBarPNAsc,
  twoBarPNDesc,
} from "src/lib/constans";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { setUniqueData } from "src/Store/reducer";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/Store/store";
import GradientBtn from "src/Components/WebsiteComponents/GradientBtn";
import DatePickerComponent from "src/Components/DatePickerComponent";
import { updateHealthProfile } from "src/Store/realTimeState";
import { RootState } from "src/Store/store";
import CustomRadio from "../CustomRadio";

// SingleBioMarkerResult component definition
const SingleBioMarkerResult: React.FC<{
  item: IAddSingleBio;
  uid: string;
  onSuccess: () => void;
  _gender: string;
  _date: string;
  isCustomer?: boolean;
  index?: number;
  customId?: string;
  type?: string;
  isFavId?: string;
}> = ({
  item,
  uid,
  onSuccess,
  _date,
  _gender,
  isCustomer,
  index,
  customId,
  type = "biomarker",
  isFavId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const uniqueKey = Math.random(); // Generate a unique key for each form instance

  const [isLoader, setIsLoader] = useState<boolean>(false); // State to handle form loading status

  const [data, setData] = useState<{
    biomarkerId: string;
    value: string;
    date: string;
    isShown: boolean;
  }>({
    biomarkerId: item?._id || "",
    value: "",
    date: "",
    isShown: false,
  }); // Initial state for capturing biomarker data
  const { healthProfile } = useSelector(
    (state: RootState) => state.realTimeState
  );

  // Calculate the user's age based on date of birth
  const age = moment().diff(moment(_date), "years");

  // Determine the age group for selecting reference and optional values
  const ageGroup = ageCategory(age);

  // State to store reference and optional values based on gender and age group
  const [refValues, setRefValues] = useState<number[]>([]);
  const [opValues, setOpValues] = useState<number[]>([]);

  // Handle form input changes
  const handleChange = (key: string, value: string) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // API_CALL: Handle form submission for saving the biomarker result
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoader(true);
    // Make API request to save the biomarker result
    const { res, err } = await httpRequest<{
      message: string;
      profileCompletionPercentage: number;
    }>({ method: "post", path: `/healthprofile/create/${uid}`, params: data });
    if (res) {
      toast(res?.message, { type: "success" });
      dispatch(
        setUniqueData({
          key: "profileCompletionPercentage",
          value: String(res?.profileCompletionPercentage),
        })
      );
      dispatch(updateHealthProfile(!healthProfile));
      onSuccess();
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  // Effect to set data and load reference/optional values when item, date, or gender changes
  useEffect(() => {
    if (item && _date && _gender) {
      console.log("Check Item Data", item);
      setData((prev) => ({
        ...prev,
        biomarkerId: item?._id,
        isShown: item?.isShown,
      }));
      // Fetch reference and optional values based on the selected gender and age group
      setRefValues(
        item[`${_gender?.toLowerCase()}Thresholds`]?.["ref"]?.[ageGroup] || []
      );
      setOpValues(
        item[`${_gender?.toLowerCase()}Thresholds`]?.["op"]?.[ageGroup] || []
      );
    }
  }, [item, _date, _gender]);

  let classOFINP = `ref-min-button-health-vitals ${
    isCustomer ? "bg-[#F0FDF4] border-[#22C55E] !w-[50%]" : "lg:w-[5vw]"
  }`;

  let classOFResultBtn = `ref-min-button-health-vitals ${
    isCustomer
      ? "text-white theme-gradient-green-bottom !w-[50%] max-w-[100px] xl:max-w-[130px] 2xl:max-w-[150px]"
      : "lg:w-[5vw]"
  }`;

  let classOfInpuContainer = `${isCustomer ? "!w-[50%]" : "lg:w-[5vw]"}`;

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex justify-center mb-3">
        <div className="flex flex-col gap-0.5 w-full mb-1">
          <label className="font-size-16px font-Poppins-Medium text-[#2F2F31]">
            Date
          </label>
          <input
            placeholder="Date"
            value={moment(data.date).format("YYYY-MM-DD")}
            onChange={(e) => {
              if (validateNotFutureDate(e.target.value)) {
                handleChange("date", e.target.value + "T00:00:00Z");
              }
            }}
            className="date-picker-health-vitals !w-full font-size-13px theme-color-green font-Poppins-Medium"
            type="date"
            required
            id={`${isFavId || ""}${type}-result-date-picker-${customId}`}
          />
        </div>
      </div>
      {item.isShown && (
        <div
          className={` mb-3 ${
            isCustomer
              ? "grid grid-cols-2 gap-2"
              : "ref-min-main-wrapper-health-vitals"
          }`}
        >
          {/* ref min */}
          <div className={`ref-min-container-health-vitals`}>
            <button
              type="button"
              className={`${classOFINP} font-size-13px font-Poppins-Regular`}
            >
              Ref.Min &lt;
            </button>
            <input
              disabled
              value={refValues?.[0] || ""}
              type="text"
              className={`ref-min-input-health-vitals font-size-13px ${classOfInpuContainer}`}
              placeholder="30"
              id={`${isFavId || ""}${type}-result-ref-min-input-${customId}`}
            />
          </div>
          {/* ref max */}
          <div className={`ref-min-container-health-vitals`}>
            <button
              type="button"
              className={`${classOFINP} font-size-13px font-Poppins-Regular`}
            >
              Ref.Max &lt;
            </button>
            <input
              disabled
              value={refValues?.[1] || ""}
              type="text"
              className={`ref-min-input-health-vitals font-size-13px ${classOfInpuContainer}`}
              placeholder="30"
              id={`${isFavId || ""}${type}-result-ref-max-input-${customId}`}
            />
          </div>
          {/* opt min */}
          <div className={`ref-min-container-health-vitals`}>
            <button
              type="button"
              className={`${classOFINP} font-size-13px font-Poppins-Regular`}
            >
              Opt.Min &lt;
            </button>
            <input
              disabled
              value={opValues?.[0] || ""}
              type="text"
              className={`ref-min-input-health-vitals font-size-13px ${classOfInpuContainer}`}
              placeholder="30"
              id={`${isFavId || ""}${type}-result-opt-min-input-${customId}`}
            />
          </div>
          {/* opt max */}
          <div className={`ref-min-container-health-vitals`}>
            <button
              type="button"
              className={`${classOFINP} font-size-13px font-Poppins-Regular`}
            >
              Opt.Max &lt;
            </button>
            <input
              disabled
              value={opValues?.[1] || ""}
              type="text"
              className={`ref-min-input-health-vitals font-size-13px ${classOfInpuContainer}`}
              placeholder="30"
              id={`${isFavId || ""}${type}-result-opt-max-input-${customId}`}
            />
          </div>
        </div>
      )}
      <div className="flex justify-center">
        <div
          className={`ref-min-container-health-vitals`}
          // className={`ref-min-container-health-vitals ${
          //   (item.barId === twoBarDNDAsc ||
          //     item.barId === twoBarDNDDesc ||
          //     item.barId === twoBarPNAsc ||
          //     item.barId === twoBarPNDesc) &&
          //   isCustomer
          //     ? ""
          //     : ""
          // }`}
        >
          <button
            type="button"
            className={`${classOFResultBtn} font-size-13px font-Poppins-Regular`}
          >
            Result &lt;
          </button>
          {item.barId === twoBarDNDAsc || item.barId === twoBarDNDDesc ? (
            <>
              <div className="flex items-center">
                <CustomRadio
                  checked={data.value === "1"}
                  onChange={(e) =>
                    handleChange("value", e ? String(1) : String(0))
                  }
                  id={`detected${uniqueKey}`}
                />
                <label
                  htmlFor={`detected${uniqueKey}`}
                  className="ml-1 lg:ml-[0.3vw] font-size-15px whitespace-nowrap font-Poppins-Regular text-gray-900 dark:text-gray-300"
                  id={`${type}-result-detected-label-${customId}`}
                >
                  Detected
                </label>
              </div>
              <div className="flex items-center">
                {/* <input
                  id={`not-detected${uniqueKey}`}
                  type="radio"
                  name={`dndresult${uniqueKey}`}
                  className="w-4 h-4"
                  onChange={(e) =>
                    handleChange(
                      "value",
                      e.target.checked ? String(0) : String(1)
                    )
                  }
                  required
                /> */}
                <CustomRadio
                  checked={data.value === "0"}
                  onChange={(e) =>
                    handleChange("value", e ? String(0) : String(1))
                  }
                  id={`not-detected${uniqueKey}`}
                  required
                />
                <label
                  htmlFor={`not-detected${uniqueKey}`}
                  className="ml-1 lg:ml-[0.3vw] font-size-15px whitespace-nowrap font-Poppins-Regular text-gray-900 dark:text-gray-300"
                  id={`${type}-result-not-detected-label-${customId}`}
                >
                  Not Detected
                </label>
              </div>
            </>
          ) : item.barId === twoBarPNAsc || item.barId === twoBarPNDesc ? (
            <>
              <div className="flex items-center">
                <CustomRadio
                  checked={data.value === "1"}
                  onChange={(e) =>
                    handleChange("value", e ? String(1) : String(0))
                  }
                  id={`positive${uniqueKey}`}
                  required
                />
                <label
                  htmlFor={`positive${uniqueKey}`}
                  className="ml-1 lg:ml-[0.3vw] font-size-15px whitespace-nowrap font-Poppins-Regular text-gray-900 dark:text-gray-300"
                  id={`${type}-result-positive-label-${customId}`}
                >
                  Positive
                </label>
              </div>
              <div className="flex items-center">
                <CustomRadio
                  checked={data.value === "0"}
                  onChange={(e) =>
                    handleChange("value", e ? String(0) : String(1))
                  }
                  id={`negative${uniqueKey}`}
                  required
                />
                <label
                  htmlFor={`negative${uniqueKey}`}
                  className="ml-1 lg:ml-[0.3vw] font-size-15px whitespace-nowrap font-Poppins-Regular text-gray-900 dark:text-gray-300"
                  id={`${type}-result-negative-label-${customId}`}
                >
                  Negative
                </label>
              </div>
            </>
          ) : (
            <input
              type={item.barId == descriptiveResult ? "text" : "number"}
              inputMode={item.barId == descriptiveResult ? "text" : "numeric"}
              className={`ref-min-input-health-vitals font-size-13px ${classOfInpuContainer}`}
              placeholder="Enter result"
              value={data.value}
              onChange={(e) => {
                let value = e.target.value;
                if (item.barId === descriptiveResult) {
                  handleChange("value", value);
                } else {
                  if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
                    handleChange("value", value);
                  }
                }
              }}
              onKeyDown={(e) => {
                // Prevent entering "e", "-", "+", or multiple "."
                if (["e", "E", "-", "+"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              required
              id={`${isFavId || ""}${type}-result-result-input-${customId}`}
            />
          )}
        </div>
      </div>
      <div className="flex justify-end my-3">
        {!isCustomer ? (
          <PrimaryBtn
            loader={isLoader}
            type="submit"
            ButtonText="Save"
            btnExtraStyle={{
              backgroundColor: "#004540",
              borderRadius: "1.5rem",
              paddingLeft: "2rem",
              paddingRight: "2rem",
            }}
          />
        ) : (
          <GradientBtn
            ButtonText="Save"
            type="submit"
            loader={isLoader}
            disabled={isLoader}
            btnExtraStyle={{
              width: "100%",
            }}
            id={`${isFavId || ""}${type}-result-save-btn-${customId}`}
          />
        )}
      </div>
    </form>
  );
};

export default SingleBioMarkerResult;
